import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { Underlying } from 'models/underlying';

import type { SolvingModeDetail, TenorDetails } from '../details/details.model';
import type { DashboardWorkspaceType } from '../infos/infos.model';
import type { SolveResultFormat } from '../results/result.model';
import type { StatusFilter } from './filter/filter.model';
export type TimeInterval = Readonly<{
  start: number;
  durationInMs: number;
}>;

export type RegulationStatus = 'Pending' | 'Success' | 'Failure' | 'NotAvailable';
export type SuccessRegulationStatusWithLinks = { status: 'Success'; links: ReadonlyArray<[lang: string, uri: string]> };
export type RegulationDocumentStatus = RegulationStatus | SuccessRegulationStatusWithLinks;

export function canBeDownloaded(status: RegulationDocumentStatus): status is SuccessRegulationStatusWithLinks {
  return (status as SuccessRegulationStatusWithLinks)?.links !== undefined;
}

export type RegulationData = Readonly<{
  kid: RegulationDocumentStatus;
  tm: RegulationDocumentStatus;
}>;

export type TileFormData = Readonly<{
  clientName?: string;
  clientId?: string;
  product: string;
  subProduct: string;
  wrapper: string;
  underlyings?: Underlying[];
  maturityDate: string;
  tenor: TenorDetails;
  currency: string;
  workspace: DashboardWorkspaceType;
  externalQuoteId: string;
  lastUpdated: Date;
  remunerationMode: string;
  fixedAmountInPercent: number;
}>;

export type QuoteData = Readonly<{
  solveMode: SolvingModeDetail;
  solveResult: number;
  solveResultFormat: SolveResultFormat;
  xMargin: number;
}>;

export type QuotingTileData = TileFormData &
  Readonly<{
    status: 'Quoting';
    solveMode: SolvingModeDetail;
    pricing: TimeInterval;
    isTradeable: boolean;
  }>;

export type QuotedTileData = TileFormData &
  Readonly<{
    status: 'Quoted';
    isTradeable: boolean;
  }> &
  QuoteData;

export type RejectedWithoutPriceTileData = TileFormData &
  Readonly<{
    status: 'RejectedWithoutPrice';
    solveMode: SolvingModeDetail;
    errorMessages: NextWorkflowError[];
    shouldUseRejectionCode: boolean;
    shouldDisplayErrorDetails: boolean;
  }>;

export type RejectedWithPriceTileData = TileFormData &
  Readonly<{
    status: 'RejectedWithPrice';
    solveMode: SolvingModeDetail;
    errorMessages: NextWorkflowError[];
    shouldUseRejectionCode: boolean;
    shouldDisplayErrorDetails: boolean;
  }> &
  Partial<QuoteData>;

export type TechnicalErrorWithoutPriceTileData = TileFormData &
  Readonly<{
    status: 'TechnicalErrorWithoutPrice';
    solveMode: SolvingModeDetail;
    errorMessage: ApiErrorMessage;
  }>;

export type TechnicalErrorWithPriceTileData = TileFormData &
  Readonly<{
    status: 'TechnicalErrorWithPrice';
    solveMode: SolvingModeDetail;
    errorMessage: ApiErrorMessage;
  }> &
  Partial<QuoteData>;

export type TradeableTileData = TileFormData &
  Readonly<{
    status: 'Tradeable';
    tradeAuth: TimeInterval;
    canTrade: boolean;
  }> &
  QuoteData;

export type TradingTileData = TileFormData &
  Readonly<{
    status: 'Trading';
  }> &
  QuoteData;

export type TradedTileData = TileFormData &
  Readonly<{
    status: 'Traded';
  }> &
  QuoteData;

export type TileData =
  | QuotingTileData
  | QuotedTileData
  | RejectedWithoutPriceTileData
  | RejectedWithPriceTileData
  | TradeableTileData
  | TechnicalErrorWithoutPriceTileData
  | TradingTileData
  | TradedTileData
  | TechnicalErrorWithPriceTileData;

export type TileStatus = TileData['status'];

export type TileCompletionStatus = 'inprogress' | 'completed' | 'rejected';

type ByStatusFilter = Readonly<{
  type: 'byStatus';
  status: StatusFilter;
}>;

type ByPinnedFilter = Readonly<{
  type: 'byPinned';
  pinnedGroup?: string;
}>;

type ByClientFilter = Readonly<{
  type: 'byClient';
  client?: string;
  product?: string;
}>;

export type DashboardFilter = ByStatusFilter | ByPinnedFilter | ByClientFilter;
